import { useCallback, useEffect, useState } from "react";
import OtpInput from "react-otp-input";
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Form,
  Label,
  Spinner,
} from "reactstrap";
import { withRouter, Link, useParams, useHistory } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";

import { ParticlesAuth } from "../AuthenticationInner";
import { ActivityButton, AppLogo } from "../../components/Common";
import { EncryptionService } from "../../services";
import { VerificationRequest } from "../../interfaces";
import { VerificationMethodEnum, VerificationTypeEnum } from "../../enums";
import {
  useVerifyOtpMutation,
  useResendOtpMutation,
} from "../../store/api/authApi";
import LanguageDropdown from "../../components/Common/LanguageDropdown";
import { toast } from "react-toastify";

const VerificationPageComponent = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [otp, setOtp] = useState("");
  const { phone: encPhone } = useParams<any>();
  const [displayPhone, setDisplayPhone] = useState<string>();
  document.title = `${t("documentTitle", { pageName: "verifyPhone" })}`;

  const [
    verifyOtp,
    {
      isLoading: isLoadingVerify,
      isError: isErrorVerify,
      isSuccess: isSuccessVerify,
      error: errorVerify,
    },
  ] = useVerifyOtpMutation();
  const [
    resendOtp,
    {
      isLoading: isLoadingResend,
      isError: isErrorResend,
      isSuccess: isSuccessResend,
      error: errorResend,
    },
  ] = useResendOtpMutation();

  const [verificationRequest, setVerificationRequest] =
    useState<VerificationRequest>({
      type: VerificationTypeEnum.RESET_PASSWORD,
      method: VerificationMethodEnum.PHONE,
    });

  //#region form
  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      countryCode: verificationRequest.countryCode,
      phoneNumber: verificationRequest.phoneNumber,
      type: verificationRequest.type,
      method: verificationRequest.method,
      code: verificationRequest.code,
    },
    validationSchema: Yup.object({
      code: Yup.string(),
    }),
    onSubmit: (values) => {
      if (otp.length < 5) return;
      verifyOtp({
        ...values,
        code: otp,
        countryCode: verificationRequest.countryCode,
        phoneNumber: verificationRequest.phoneNumber,
      });
    },
  });

  //#endregion
  useEffect(() => {
    if (encPhone) {
      try {
        const decryPhone: string = EncryptionService.decrypt(encPhone);
        const phoneArray: string[] = decryPhone.split("-");
        if (phoneArray && phoneArray.length === 2) {
          setVerificationRequest({
            ...verificationRequest,
            countryCode: phoneArray[0],
            phoneNumber: phoneArray[1],
          });
          setDisplayPhone(`+${phoneArray[0]} ${phoneArray[1]}`);
        }
      } catch (error) {
        history.goBack();
      }
    }
  }, [encPhone]);

  const resendVerificationCode = useCallback(() => {
    resendOtp({
      countryCode: verificationRequest.countryCode,
      phoneNumber: verificationRequest.phoneNumber,
      method: VerificationMethodEnum.PHONE,
      type: VerificationTypeEnum.RESET_PASSWORD,
    });
  }, [
    resendOtp,
    verificationRequest.countryCode,
    verificationRequest.phoneNumber,
  ]);

  useEffect(() => {
    if (!isLoadingVerify && isSuccessVerify) {
      const encCode = EncryptionService.encrypt(otp);
      history.push(`/reset-password/${encPhone}/${encCode}`);
    }
  }, [isLoadingVerify, isSuccessVerify]);

  useEffect(() => {
    if (!isLoadingResend && isSuccessResend) {
      toast.success(t("OtpResentSuccessfully"));
    }
  }, [isLoadingResend, isSuccessResend]);

  return (
    <ParticlesAuth>
      <div className="auth-page-content">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <div className="text-center mt-1">
                    <AppLogo />
                    <h5 className="text-primary mt-2">
                      {t("verifyYourPhone")}
                    </h5>
                    <p className="text-muted">
                      {t("enterVerificationCode")}
                      <span dir="ltr">{displayPhone}</span>
                    </p>
                  </div>
                  <div className="mt-4">
                    <div className="justify-content-center align-items-center d-flex">
                      <LanguageDropdown />
                    </div>
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        form.handleSubmit();
                        return false;
                      }}
                    >
                      <div className="mb-3">
                        <OtpInput
                          value={otp}
                          onChange={setOtp}
                          numInputs={5}
                          renderSeparator={<span className="px-1 bold"></span>}
                          renderInput={(props) => <input {...props} />}
                          inputType={"number"}
                          inputStyle={{
                            width: "20%",
                            height: "50px",
                            fontSize: "1.5rem",
                            borderColor: "#f3f6f9",
                            borderStyle: "solid",
                            borderRadius: "5px",
                            borderWidth: 0.2,
                            backgroundColor: "#f3f6f9",
                          }}
                        />
                      </div>
                      <div className="text-center mt-4">
                        <ActivityButton
                          title="verify"
                          inProgressTitle="verifying"
                          color="success"
                          className="btn btn-success w-100"
                          type="submit"
                          inProgress={isLoadingVerify}
                          disabled={isLoadingVerify}
                        ></ActivityButton>
                        <div className="mt-3">
                          {isLoadingResend ? (
                            <Spinner
                              size="sm"
                              className="flex-shrink-0"
                              role="status"
                            >
                              {t("inProgress")}...
                            </Spinner>
                          ) : (
                            <Link
                              to="#"
                              onClick={resendVerificationCode}
                              className="text-muted"
                            >
                              {t("resendVerificationCode")}
                            </Link>
                          )}
                        </div>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-4 text-center">
                <p className="mb-0 text-white">
                  {t("backToLogin")}...{" "}
                  <Link
                    to="/login"
                    className="fw-semibold text-white text-decoration-underline"
                  >
                    {" "}
                    {t("clickHere")}{" "}
                  </Link>{" "}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export const VerificationPage = withRouter(VerificationPageComponent);
